/**
 * Performance Utilities
 * Helpers for optimizing performance and monitoring Core Web Vitals
 */

// Debounce function for scroll and resize events
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle function for frequent events
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Lazy load images with Intersection Observer
export function lazyLoadImages() {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach((img) => {
      imageObserver.observe(img);
    });
  }
}

// Preload critical resources
export function preloadResource(href, as = 'fetch', crossorigin = false) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  if (crossorigin) {
    link.crossOrigin = 'anonymous';
  }
  document.head.appendChild(link);
}

// Prefetch next page
export function prefetchPage(href) {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  document.head.appendChild(link);
}

// Monitor Core Web Vitals
export function reportWebVitals(onPerfEntry) {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    });
  }
}

// Check if user is on slow connection
export function isSlowConnection() {
  if ('connection' in navigator) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return (
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g' ||
      connection.saveData === true
    );
  }
  return false;
}

// Load font asynchronously
export function loadFont(fontFamily, fontUrl) {
  if (document.fonts && document.fonts.check) {
    if (!document.fonts.check(`16px "${fontFamily}"`)) {
      const link = document.createElement('link');
      link.href = fontUrl;
      link.rel = 'stylesheet';
      link.media = 'print';
      link.onload = () => {
        link.media = 'all';
        document.body.classList.add('font-loaded');
      };
      document.head.appendChild(link);
    } else {
      document.body.classList.add('font-loaded');
    }
  }
}

// Minimize DOM manipulation
export function batchDOMUpdates(callback) {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

// Optimize scroll performance
export function optimizeScroll(handler) {
  let ticking = false;
  return function optimizedHandler(...args) {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handler(...args);
        ticking = false;
      });
      ticking = true;
    }
  };
}

// Load non-critical scripts after page load
export function loadScriptAsync(src, async = true, defer = true) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = async;
    script.defer = defer;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}







